// Deal Pipeline spec §6.4 — per-row attestation popup. Replaces the
// per-action menu (Send / Remind / Approve / Reject / Waive) on the
// Document Requests table with a single dialog where B1 captures the
// outcome of an offline document handoff.

import { useEffect, useMemo, useState } from "react";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { documentTypeLabel } from "@/api/documentTypes";
import { DOC_REQUEST_REASONS } from "@/api/dealLookups";
import { useAttestDocumentRequest } from "@/hooks/deals/useDealDetail";
import { calendarDateInputToIso, useDateFormatter } from "@/utils/datetime";
import type {
  DocumentRequestAttestStatus,
  PreApprovalDocumentRequestDto,
} from "@/types/deals";

interface Props {
  open: boolean;
  dealId: string;
  request: PreApprovalDocumentRequestDto | null;
  onClose: () => void;
}

const STATUS_OPTIONS: { value: DocumentRequestAttestStatus; label: string }[] = [
  { value: "Requested", label: "Pending — outstanding" },
  { value: "Received", label: "Received" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
  { value: "Waived", label: "Waived" },
];

function describeApi(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.errors.length) return err.errors.join("; ");
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function defaultStatus(req: PreApprovalDocumentRequestDto): DocumentRequestAttestStatus {
  // Map the existing schema status onto the popup's reduced set. Reminded
  // collapses back to Requested (popup never surfaces "Reminded"); the
  // long-deprecated UnderReview lands on Received.
  switch (req.status) {
    case "Approved":
      return "Approved";
    case "Rejected":
      return "Rejected";
    case "Waived":
      return "Waived";
    case "Received":
    case "UnderReview":
      return "Received";
    case "Reminded":
    case "Requested":
    case "Draft":
    default:
      return "Requested";
  }
}

export function DocumentRequestPopup({ open, request, onClose }: Props) {
  const toast = useToast();
  const attest = useAttestDocumentRequest(request?.dealId ?? "");
  const { dateTime } = useDateFormatter();

  const [target, setTarget] = useState<DocumentRequestAttestStatus>("Requested");
  const [notes, setNotes] = useState("");
  const [receivedDate, setReceivedDate] = useState("");
  const [receivedFrom, setReceivedFrom] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && request) {
      setTarget(defaultStatus(request));
      setNotes(request.notes ?? "");
      setReceivedDate(
        request.receivedDate ? request.receivedDate.slice(0, 10) : "",
      );
      setReceivedFrom("");
      setReasonCode(request.reasonCode ?? "");
      setError(null);
    }
  }, [open, request]);

  const showReceivedFields = useMemo(
    () => target === "Received" || target === "Approved",
    [target],
  );
  const requireNotes = target === "Rejected" || target === "Waived";
  const requireReason = target === "Rejected" || target === "Waived";

  if (!request) return null;

  async function submit() {
    if (!request) return;
    if (requireReason && !reasonCode) {
      setError(
        target === "Rejected"
          ? "Pick a rejection reason."
          : "Pick a waiver reason.",
      );
      return;
    }
    if (requireNotes && notes.trim().length < 10) {
      setError(
        "Add at least 10 characters of notes so the audit trail captures the rationale.",
      );
      return;
    }
    setError(null);
    try {
      await attest.mutateAsync({
        id: request.id,
        body: {
          targetStatus: target,
          notes: notes.trim() || null,
          receivedDate: showReceivedFields ? calendarDateInputToIso(receivedDate) : null,
          receivedFromContact:
            showReceivedFields && receivedFrom.trim()
              ? receivedFrom.trim()
              : null,
          reasonCode: requireReason ? reasonCode : null,
        },
      });
      toast.success("Document request updated.");
      onClose();
    } catch (err) {
      setError(describeApi(err, "Update failed."));
    }
  }

  const lastUpdate = request.decisionDate ?? request.receivedDate ?? request.requestedDate ?? null;
  const lastUpdateLabel = lastUpdate
    ? dateTime(lastUpdate)
    : "Never";

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Update document request"
      description={documentTypeLabel(request.documentTypeRefCode)}
      size="md"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={attest.isPending}>
            Cancel
          </Button>
          <Button
            variant={target === "Rejected" ? "destructive" : "primary"}
            onClick={submit}
            loading={attest.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <div className="grid grid-cols-2 gap-space-3 text-small">
          <ReadField label="Requested from">{request.requestedFromParty}</ReadField>
          <ReadField label="Required">
            {request.required ? (
              <Badge status="info">Required</Badge>
            ) : (
              <Badge status="archived">Optional</Badge>
            )}
          </ReadField>
        </div>

        <FormField label="Status" required>
          <Select
            value={target}
            onChange={(e) =>
              setTarget(e.target.value as DocumentRequestAttestStatus)
            }
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormField>

        {showReceivedFields && (
          <div className="grid grid-cols-2 gap-space-3">
            <FormField label="Received date">
              <Input
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
              />
            </FormField>
            <FormField
              label="Received from"
              helperText="Who provided this? (e.g. Broker via portal, Borrower via email.)"
            >
              <Input
                type="text"
                value={receivedFrom}
                onChange={(e) => setReceivedFrom(e.target.value)}
                placeholder="Broker — emailed 5/15"
              />
            </FormField>
          </div>
        )}

        {requireReason && (
          <FormField
            label={target === "Rejected" ? "Rejection reason" : "Waiver reason"}
            required
          >
            <Select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
            >
              <option value="">Select a reason…</option>
              {DOC_REQUEST_REASONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.displayName}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        <FormField
          label="Notes"
          required={requireNotes}
          helperText={
            requireNotes
              ? "Captured in the audit trail; explain what the requester needs to address."
              : "Optional context."
          }
        >
          <Textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              target === "Received"
                ? "What was received and from whom?"
                : target === "Rejected"
                  ? "What's wrong and what should they re-send?"
                  : ""
            }
          />
        </FormField>

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}

        <div className="text-caption text-gray-mid pt-space-2 border-t border-border-light">
          Last update: {lastUpdateLabel}
          {request.reviewerId ? ` by ${request.reviewerId}` : ""}.
        </div>
      </div>
    </Modal>
  );
}

function ReadField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="text-onyx mt-[2px]">{children}</div>
    </div>
  );
}
